import { useRef, useCallback } from "react";

/**
 * Long-press (~0.5s) gesture hook — WhatsApp-style message actions.
 * - Touch: fires after `ms` unless the finger moves >12px (scroll) or lifts.
 * - Desktop: right-click (contextmenu) fires immediately.
 * - Suppresses the synthetic click that follows a long-press so taps that
 *   open viewers (photos/videos/PDFs) don't fire after the menu opens.
 */
export function useLongPress(callback, { ms = 500 } = {}) {
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const firedRef = useRef(false);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const onTouchStart = useCallback((e) => {
    if (e.touches && e.touches.length > 1) { clear(); return; }
    const t = e.touches ? e.touches[0] : e;
    startRef.current = { x: t.clientX, y: t.clientY };
    firedRef.current = false;
    clear();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      firedRef.current = true;
      try { if (navigator.vibrate) navigator.vibrate(12); } catch (err) { /* ignore */ }
      callback();
    }, ms);
  }, [callback, ms]);

  const onTouchMove = useCallback((e) => {
    const s = startRef.current;
    if (!s || !timerRef.current) return;
    const t = e.touches ? e.touches[0] : e;
    if (Math.hypot(t.clientX - s.x, t.clientY - s.y) > 12) clear();
  }, []);

  const onTouchEnd = useCallback(() => clear(), []);

  // Swallow the click generated right after a long-press
  const onClickCapture = useCallback((e) => {
    if (firedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      firedRef.current = false;
    }
  }, []);

  const onContextMenu = useCallback((e) => {
    e.preventDefault();
    if (firedRef.current) { firedRef.current = false; return; } // mobile long-press already fired
    callback();
  }, [callback]);

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd, onClickCapture, onContextMenu };
}
